import { Dialog, DialogContent } from './ui/dialog';
import { useEffect, useState } from 'react';

interface VideoLightboxProps {
  /**
   * The URL of the video to embed
   */
  videoUrl: string;
  /**
   * Whether the lightbox is open
   */
  isOpen: boolean;
  /**
   * Callback when the lightbox is closed
   */
  onOpenChange: (open: boolean) => void;
  /**
   * Whether to autoplay the video when opened
   * @default true
   */
  autoplay?: boolean;
  /**
   * Whether to mute the video
   * @default false
   */
  mute?: boolean;
  /**
   * Custom CSS class for the lightbox
   */
  className?: string;
  /**
   * Custom aspect ratio (percentage for padding-bottom)
   * @default 65
   */
  aspectRatio?: number;
  /**
   * Time offset in seconds to start the video from
   * @default 0
   */
  timeOffset?: number;
}

interface BuildVideoSrcParams {
  baseUrl: string;
  autoplay: boolean;
  mute: boolean;
  timeOffset: number;
}

const buildVideoSrc = ({
  baseUrl,
  autoplay,
  mute,
  timeOffset,
}: BuildVideoSrcParams): string => {
  const url = new URL(baseUrl);
  if (autoplay) {
    url.searchParams.set('autoplay', '1');
  }
  url.searchParams.set('mute', mute ? '1' : '0');
  url.searchParams.set('t', timeOffset.toString());
  return url.toString();
};

export function VideoLightbox({
  videoUrl,
  isOpen,
  onOpenChange,
  autoplay = true,
  mute = false,
  className = '',
  aspectRatio = 65,
  timeOffset = 0,
}: VideoLightboxProps) {
  const [iframeKey, setIframeKey] = useState<string>(Date.now().toString());

  useEffect(() => {
    if (isOpen) {
      setIframeKey(Date.now().toString());
    }
  }, [isOpen]);

  const videoSrc = buildVideoSrc({
    baseUrl: videoUrl,
    autoplay,
    mute,
    timeOffset,
  });

  return (
    <Dialog
      open={isOpen}
      onOpenChange={onOpenChange}
    >
      <DialogContent
        className={`w-[95%] max-w-[1200px] border-0 bg-transparent p-2 shadow-none sm:p-4 [&>button]:hidden ${className}`}
      >
        <div
          className={`relative w-full overflow-hidden`}
          style={{ paddingBottom: `${aspectRatio}%` }}
        >
          {isOpen && (
            <iframe
              key={iframeKey}
              src={videoSrc}
              frameBorder="0"
              allowFullScreen
              allow="autoplay; fullscreen"
              className="absolute left-0 top-0 h-full w-full rounded-md shadow-2xl"
            ></iframe>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
